import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { fortnoxCallback } from 'src/shared/services/accountingIntegrationService';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';

export const FortnoxCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['invoice']);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // Kolla om Fortnox returnerade ett fel
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      const message = errorDescription ? `Fortnox: ${errorDescription} (${error})` : `Fortnox: ${error}`;
      toast.error(message, { autoClose: false });
      navigate(RoutesEnum.AdminInvoiceGroupSettings);
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      toast.error(t('invoice:accountingIntegration.connectError') as string);
      navigate(RoutesEnum.AdminInvoiceGroupSettings);
      return;
    }

    // state = base64("{custName}|{ownerId}")
    let ownerId = 0;
    try {
      const decoded = atob(state);
      const parts = decoded.split('|');
      ownerId = Number(parts[1]);
    } catch {
      toast.error(t('invoice:accountingIntegration.connectError') as string);
      navigate(RoutesEnum.AdminInvoiceGroupSettings);
      return;
    }

    const exchangeCode = async () => {
      try {
        await fortnoxCallback(code, ownerId);
        toast.success(t('invoice:accountingIntegration.connectSuccess') as string);
        navigate(`${RoutesEnum.AdminInvoiceGroupSettings}/${ownerId}/accounting?openSettings=true`);
      } catch (err: any) {
        const serverMessage = err?.response?.data?.error;
        const message = serverMessage
          ? `${t('invoice:accountingIntegration.connectError')}: ${serverMessage}`
          : (t('invoice:accountingIntegration.connectError') as string);
        toast.error(message, { autoClose: false });
        navigate(`${RoutesEnum.AdminInvoiceGroupSettings}/${ownerId}/accounting`);
      }
    };

    exchangeCode();
  }, [searchParams, navigate, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant='body1'>{t('invoice:accountingIntegration.connecting')}</Typography>
    </Box>
  );
};
